import React, {useState, useEffect} from 'react';
import {useRouter} from 'next/router';
import cx from 'classnames';
import * as Sentry from '@sentry/react';

import {ShieldCheckIcon} from '@heroicons/react/outline';
import Logo from '../../../public/images/logo.svg';

import Link from '../../components/Link';
import Button from '../../components/ButtonNew';
import DocumentHeader from '../../components/DocumentHeader';
import {BASE_URL} from '../../constants/config';
import {productRecommendationBySku, upgradeCards, addOnCards} from './constants';

import {useAppContext, useAnalytics} from '../../hooks';

const DEFAULT_RETURN_PATH = '/upgrade';

export const startPreCheckout = (router, {cart, returnPath, tag}, tracker) => {
  const mainItem = cart?.[0]?.id;
  tracker?.upgradeClickBuy({product: mainItem, tag});

  const payload = encodeURIComponent(JSON.stringify({cart, returnPath, tag}));
  router.push(`/precheckout?checkout=${payload}`);
};

const Item = ({
  title, description, price, priceSecondaryText, linkText, linkHref,
  selected, onClick, isUpgrade = false,
}) => {
  const cta = isUpgrade ? `Upgrade to ${price}${priceSecondaryText || ''}` : `Add for ${price}${priceSecondaryText || ''}`;

  return (
    <div className="border rounded-lg p-4 shadow w-full flex flex-col gap-y-4 sm:flex-row justify-between items-center">
      <div className="sm:mr-4">
        <h3 className="text-xl font-semibold">{title}</h3>
        <p className="text-base text-gray-500 mt-1">
          {description}
          {linkText && linkHref && (
            <>
              {' '}
              <Link href={linkHref} target="_blank" className="text-indigo font-medium">
                {linkText}
              </Link>
            </>
          )}
        </p>
      </div>
      <div className="w-full sm:w-auto">
        <button
          type="button"
          onClick={onClick}
          className={cx(
            'w-full sm:w-48 py-4 text-base font-semibold rounded-lg transition duration-300', {
              'bg-indigo-100 text-indigo-600 hover:bg-indigo-200': !isUpgrade && !selected,
              'bg-blue-100 text-blue-600 hover:bg-blue-200': isUpgrade && !selected,
              'bg-red-100 text-red-600 hover:bg-red-200': selected,
            },
          )}
        >
          {selected ? 'Remove' : cta}
        </button>
      </div>
    </div>
  );
};

const PreCheckoutPage = () => {
  const {
    catalog, currency, onCheckout, currentUser, hasSubscription,
  } = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();

  const [cart, setCart] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [upgrades, setUpgrades] = useState([]);
  const [returnPath, setReturnPath] = useState(null);
  const [tag, setTag] = useState(null);
  const [error, setError] = useState(null);

  const cancelUrl = `${BASE_URL}${returnPath || DEFAULT_RETURN_PATH}?payment_canceled=true`;

  const formatListingItems = ({
    skus, cards, catalog: c, hasSubscription: h, membershipSkus,
  }) => skus
    .filter((sku) => !h || !membershipSkus.has(sku))
    .map((sku) => {
      const card = cards[sku];
      if (!card) return null;
      return {
        ...card,
        sku,
        price: c[sku]?.priceString,
        selected: false,
      };
    })
    .filter(Boolean);

  useEffect(() => {
    if (!router.isReady) return;

    const {checkout} = router.query;

    if (checkout) {
      try {
        const deserializedCheckout = JSON.parse(decodeURIComponent(checkout));
        if (deserializedCheckout) {
          setCart(Array.isArray(deserializedCheckout.cart) ? deserializedCheckout.cart : []);
          setReturnPath(deserializedCheckout.returnPath || DEFAULT_RETURN_PATH);
          setTag(deserializedCheckout.tag || null);
        }
      } catch (err) {
        setError('Invalid cart. Please contact support@tryexponent.com for assistance.');
        Sentry.captureException(err);
      }
    }
  }, [router.isReady]);

  useEffect(() => {
    if (!cart.length || !catalog) return;

    const mainItem = cart[0].id;

    const recommendation = productRecommendationBySku[mainItem] || {};
    const {upgrades: u = [], addOns: a = []} = recommendation;

    const membershipSkus = new Set(
      [catalog.MEMBERSHIP_MONTHLY?.name, catalog.MEMBERSHIP_ANNUAL?.name],
    );

    const upgradeListings = formatListingItems({
      skus: u,
      cards: upgradeCards,
      catalog,
      hasSubscription,
      membershipSkus,
    });

    const addOnListings = formatListingItems({
      skus: a,
      cards: addOnCards,
      catalog,
      hasSubscription,
      membershipSkus,
    });

    setUpgrades(upgradeListings);
    setAddOns(addOnListings);

    if (!upgradeListings.length && !addOnListings.length) {
      onCheckout(cart, currentUser, {cancelUrl, tag}, undefined, true);
    } else {
      tracker.precheckoutView({product: mainItem});
    }
  }, [cart, catalog]);

  const handleToggleUpgrade = (sku) => {
    setUpgrades((prev) => prev.map(
      (item) => (item.sku === sku ? {...item, selected: !item.selected} : item),
    ));
  };

  const handleToggleAddOn = (sku) => {
    setAddOns((prev) => prev.map(
      (item) => (item.sku === sku ? {...item, selected: !item.selected} : item),
    ));
  };

  const handleCheckout = () => {
    let finalCart = [...cart];

    // Upgrades: Replace the current item in cart
    const selectedUpgrades = upgrades.filter((item) => item.selected);
    if (selectedUpgrades.length > 0) {
      finalCart = selectedUpgrades.map((item) => ({id: item.sku, quantity: 1}));
    }

    // Add-Ons: Add to the cart
    const selectedAddOns = addOns.filter((item) => item.selected);
    finalCart.push(...selectedAddOns.map((item) => ({id: item.sku, quantity: 1})));

    const checkoutOptions = {};

    if (cancelUrl) checkoutOptions.cancelUrl = cancelUrl;
    if (tag) checkoutOptions.tag = tag;
    if (currency?.code) checkoutOptions.currency = currency.code;

    tracker.precheckoutClickCheckout({
      product: cart[0].id, // original item
      upgrades: selectedUpgrades.map((item) => item.sku),
      addOns: selectedAddOns.map((item) => item.sku),
    });

    onCheckout(finalCart, currentUser, checkoutOptions, undefined, true);
  };

  const isLoading = !upgrades.length && !addOns.length;

  if (error) {
    return (
      <div className="bg-white flex justify-center min-h-screen">
        <DocumentHeader title="Error" noindex />
        <div className="flex gap-2 max-w-3xl w-full p-4 mt-4 sm:mt-20 justify-center">
          <img src={Logo.src} alt="Exponent Logo" className="h-6 w-6" />
          <div className="text-gray-500 text-lg">{error}</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white flex justify-center min-h-screen">
        <DocumentHeader title="Loading" noindex />
        <div className="flex gap-2 max-w-3xl w-full p-4 mt-4 sm:mt-20 justify-center">
          <img src={Logo.src} alt="Exponent Logo" className="h-6 w-6" />
          <div className="text-gray-500 text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white flex justify-center min-h-screen">
      <DocumentHeader title="Pre-Checkout" noindex />

      <div className="flex flex-col max-w-3xl w-full p-4 mt-4 sm:mt-20">
        {/* Header */}
        <div className="flex flex-col items-start">
          <Link
            href={returnPath || DEFAULT_RETURN_PATH}
            onClick={() => tracker.precheckoutCancel()}
            className="text-gray-500 text-base font-medium mb-4"
          >
            {'<- Back'}
          </Link>

          <img src={Logo.src} alt="Exponent Logo" className="h-8 w-8 mb-6" />
          <div>
            <h2 className="text-2xl font-semibold">You&apos;re almost there!</h2>
            <p className="text-base text-gray-500">Get the most out of your Exponent experience with these exclusive add-ons.</p>
          </div>
        </div>

        {/* Upsell & Cross-Sell Items */}
        <div className="flex flex-col gap-4 mt-6">
          {upgrades.map((item) => (
            <Item
              key={item.id}
              title={item.title}
              description={item.description}
              price={item.price}
              priceSecondaryText={item.priceSecondaryText}
              linkHref={item.linkHref}
              linkText={item.linkText}
              selected={item.selected}
              onClick={() => handleToggleUpgrade(item.sku)}
              isUpgrade
            />
          ))}

          {addOns.map((item) => (
            <Item
              key={item.id}
              title={item.title}
              description={item.description}
              price={item.price}
              priceSecondaryText={item.priceSecondaryText}
              linkHref={item.linkHref}
              linkText={item.linkText}
              selected={item.selected}
              onClick={() => handleToggleAddOn(item.sku)}
            />
          ))}
        </div>

        {/* Checkout Button */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-end items-center">
          <Button
            onClick={handleCheckout}
            disabled={isLoading}
            className="order-1 sm:order-2 w-full sm:w-48 py-4 text-base font-semibold"
          >
            Checkout
          </Button>
          <div className="order-2 sm:order-1 text-sm text-gray-500 text-center font-medium w-full sm:w-auto flex items-center justify-center">
            <ShieldCheckIcon className="h-5 w-5 mr-1 flex-shrink-0" />
            All items come with a money-back guarantee
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreCheckoutPage;
